// src/components/structure/DraggableRow.js
import { useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { StatefulPopover, PLACEMENT } from 'baseui/popover';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { parseTime } from '../../utils/viewHelpers';
import { MessageSquare } from 'lucide-react';
import {
  Cell,
  MenuContainer,
  StyledDraggableRow,
  StyledDragHandleCell,
} from '../../styles/controllerPageStyles';

const DraggableRow = ({
  id,
  row,
  columns,
  tableRowIndex,
  events,
  timerState,
  timerRowIndex,
  canEditThisRowContent,
  canDeleteThisRow,
  canInsertThisRow,
  canDragRow,
  onInsertRow,
  onDeleteRow,
  onInsertBreakRow,
  onChangeRow,
  onEditEvent,
}) => {
  const { t } = useLocale();
  const { config: appConfig } = useAppConfig();
  const previousValueRef = useRef(null);

  const handleTimeBlur = (e) => {
    const { value } = e.target;
    const timeKey = appConfig.requiredTimeKey;

    if (value.trim() === '') {
      return;
    }

    const timeFormatRegex = /^(\d{1,2}:)?\d{1,2}:\d{2}$/;
    if (!timeFormatRegex.test(value)) {
      alert(t('errors.invalidTimeFormat'));
      onChangeRow(tableRowIndex, timeKey, previousValueRef.current);
      return;
    }

    const seconds = parseTime(value);

    if (seconds <= 0) {
      alert(t('errors.timeCannotBeZero'));
      onChangeRow(tableRowIndex, timeKey, previousValueRef.current);
      return;
    }

    const isFirstRowBreak = tableRowIndex === 0 && row.type === 'break';
    if (!isFirstRowBreak) {
      const EIGHT_HOURS_IN_SECONDS = 28800;
      if (seconds > EIGHT_HOURS_IN_SECONDS) {
        alert(t('errors.timeExceedsLimit'));
        onChangeRow(tableRowIndex, timeKey, previousValueRef.current);
        return;
      }
    }
  };




  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: typeof canDragRow === 'function' ? !canDragRow(tableRowIndex) : true,
  });



  const RowMenu = (
    <StatefulPopover
      placement={PLACEMENT.bottomRight}
      content={({ close }) => (
        <MenuContainer>
          <Button size="compact" onClick={() => { onDeleteRow(tableRowIndex); close(); }} disabled={!canDeleteThisRow}>
            {t('structure.deleteRow')}
          </Button>
          {row.type !== 'break' && (
            <>
              <Button size="compact" onClick={() => { onInsertRow(tableRowIndex); close(); }} disabled={!canInsertThisRow}>
                {t('structure.addRowAfter')}
              </Button>
              <Button size="compact" onClick={() => { onInsertBreakRow(tableRowIndex); close(); }} disabled={!canInsertThisRow}>
                {t('structure.addBreakAfter')}
              </Button>
            </>
          )}
          {row.type === 'break' && (
            <Button size="compact" onClick={() => { onEditEvent(tableRowIndex, events); close(); }} disabled={!canEditThisRowContent}>
              {t('controller.editMessages')}
            </Button>
          )}
        </MenuContainer>
      )}
    >
      <Button size="compact" kind="tertiary" shape="square" aria-label={t('structure.rowActions')} disabled={!canEditThisRowContent && !canDeleteThisRow && !canInsertThisRow}>
        ⋮
      </Button>
    </StatefulPopover>
  );

  return (
    <StyledDraggableRow ref={setNodeRef} $transform={CSS.Transform.toString(transform)} $transition={transition} $isDragging={isDragging} {...attributes}>
      <StyledDragHandleCell {...listeners} tabIndex={0} title={typeof canDragRow === 'function' && canDragRow(tableRowIndex) ? t('structure.dragToReorderRow') : ''}>
        {typeof canDragRow === 'function' && canDragRow(tableRowIndex) ? '☰' : ''}
      </StyledDragHandleCell>

      {columns.map((col) => {
        if (row.type === 'break') {
          const breakLabel = tableRowIndex === 0 ? t('structure.preTournamentLabel') : t('common.break');

          const hasMessages = events && events.length > 0;

          if (col.label === appConfig.requiredLevelKey) {
            return (
              <Cell key={`${id}-break-level`}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                  {breakLabel}
                  {hasMessages && (
                    <StatefulPopover
                      placement={PLACEMENT.top}
                      content={() => (
                        <MenuContainer style={{ padding: '10px', maxWidth: '200px' }}>
                          {events.map((event, idx) => (
                            <div key={idx} style={{ marginBottom: '5px', wordBreak: 'break-word' }}>{event.message}</div>
                          ))}
                        </MenuContainer>
                      )}
                      triggerType="hoverAndClick"
                    >
                      <span><MessageSquare size={16} style={{ cursor: 'pointer' }} /></span>
                    </StatefulPopover>
                  )}
                </div>
              </Cell>
            );
          }
          if (col.label === appConfig.requiredTimeKey) {
            return (
              <Cell key={`${id}-break-time`}>
                <Input
                  value={row.time || ''}
                  onChange={e => onChangeRow(tableRowIndex, appConfig.requiredTimeKey, e.target.value)}
                  onFocus={(e) => { previousValueRef.current = e.target.value; }}
                  onBlur={handleTimeBlur}
                  size="compact"
                  disabled={!canEditThisRowContent}
                  type="text"
                />
              </Cell>
            );
          }
          return <Cell key={`${id}-break-${col.id}`}>—</Cell>;
        }

        if (col.label === appConfig.requiredLevelKey) {
          return <Cell key={`${id}-level`}>{row.level}</Cell>;
        }
        return (
          <Cell key={`${id}-${col.id}`}>
            <Input
              value={row[col.label] ?? ''}
              onChange={e => onChangeRow(tableRowIndex, col.label, e.target.value)}
              onFocus={col.label === appConfig.requiredTimeKey ? (e) => { previousValueRef.current = e.target.value; } : undefined}
              onBlur={col.label === appConfig.requiredTimeKey ? handleTimeBlur : undefined}
              size="compact"
              disabled={!canEditThisRowContent}
              type={['SB', 'BB', 'Ante'].includes(col.label) ? 'number' : 'text'}
              min="0"
            />
          </Cell>
        );
      })}

      <Cell>{RowMenu}</Cell>
    </StyledDraggableRow>
  );
};

export default DraggableRow;